import { memo, useCallback, useMemo, useState } from 'react';

import { ViewTypeName } from '@air/api/types';
import { CheckIcon, PinIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { formatFullName } from '@air/utilities';
import { cn } from '@air/utils-tailwind';

import { ViewTypeToggleMenuComponent } from '~/components/ViewControls/ViewTypeToggleMenu/ViewTypeToggleMenuComponent';
import { PAGE_HEADER_TOOLBAR_GRID_CLASS } from '~/components/v2/components/shared/page-header/PageHeaderToolbarGrid';
import { PrivateWorkspaceAgentAssetListCount } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListCount';
import { usePrivateWorkspaceAgentAssetListContext } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListProvider';
import { PrivateWorkspaceAgentAssetListTitle } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListTitle';
import { SaveWorkspaceAgentAssetListAsBoardModal } from '~/components/v2/components/workspace/workspace-agent-asset-list/SaveWorkspaceAgentAssetListAsBoardModal';
import {
  getSaveAsBoardActionLabel,
  WORKSPACE_AGENT_ASSET_LIST_LABEL,
} from '~/components/v2/components/workspace/workspace-agent-asset-list/workspaceAgentAssetListConstants';
import { usePrivateWorkspaceAgentPinnedArtifacts } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPinnedArtifacts';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { useAccount } from '~/swr-hooks/account/useAccount';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';

const ARTIFACT_LIST_VIEW_TYPES = [ViewTypeName.gallery, ViewTypeName.table];

const formatArtifactCapturedAt = (iso: string | undefined): string => {
  if (!iso) {
    return 'Just now';
  }
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return 'Just now';
  }
  const day = date.toLocaleDateString([], { day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return `${day}, ${time}`;
};

/**
 * Header for the agent asset list artifact view: title on top, and a single row beneath it with
 * capture time + creator on the left and item count + the shared view menu + Pin icon +
 * Save as board on the right. No search bar, no filters, no sort menu, no kanban, and no gear menu.
 */
export const PrivateWorkspaceAgentAssetListHeader = memo(() => {
  const { artifactCreatedAt, artifactId, failedCount, hasMore, isLoading, sessionId, tableItemsData, workspaceId } =
    usePrivateWorkspaceAgentAssetListContext();
  const hasUnresolvedRows = hasMore || isLoading || tableItemsData.isLoadingMore || failedCount > 0;
  const { data: currentAccount } = useAccount();
  const { canPin, isArtifactPinned, toggleArtifactPinned } = usePrivateWorkspaceAgentPinnedArtifacts({
    sessionId,
    workspaceId,
  });
  const savedBoard = useWorkspaceAgentAssetListViewStore((state) =>
    artifactId ? state.savedBoardsByArtifactId[artifactId] : undefined,
  );
  const markSavedAsBoard = useWorkspaceAgentAssetListViewStore((state) => state.markSavedAsBoard);
  const presentation = useWorkspaceAgentAssetListViewStore((state) => state.presentation);
  const setPresentation = useWorkspaceAgentAssetListViewStore((state) => state.setPresentation);
  const storedTitle = useWorkspaceAgentAssetListViewStore((state) =>
    artifactId ? state.titlesByArtifactId[artifactId] : undefined,
  );
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const isPinned = isArtifactPinned(artifactId);
  const currentViewTypeName = presentation === 'table' ? ViewTypeName.table : ViewTypeName.gallery;

  const clipIds = useMemo(() => tableItemsData.data.map(({ item }) => item.id), [tableItemsData.data]);

  const handleChangeView = useCallback(
    (viewTypeName: ViewTypeName) => {
      if (viewTypeName === ViewTypeName.gallery) {
        setPresentation('gallery');
        return;
      }
      if (viewTypeName === ViewTypeName.table) {
        setPresentation('table');
      }
    },
    [setPresentation],
  );

  const handleTogglePin = useCallback(() => {
    if (!artifactId) {
      return;
    }
    toggleArtifactPinned(artifactId);
  }, [artifactId, toggleArtifactPinned]);

  const handleSaveAsBoard = useCallback(() => {
    if (!artifactId) {
      return;
    }
    setIsSaveModalOpen(true);
  }, [artifactId]);

  const handleCloseSaveModal = useCallback(() => {
    setIsSaveModalOpen(false);
  }, []);

  const handleSavedAsBoard = useCallback(
    ({ boardId, boardTitle }: { boardId: string; boardTitle: string }) => {
      if (!artifactId) {
        return;
      }
      markSavedAsBoard(artifactId, { boardId, boardTitle });
    },
    [artifactId, markSavedAsBoard],
  );

  const capturedAtLabel = formatArtifactCapturedAt(artifactCreatedAt);
  const creatorName =
    formatFullName(currentAccount?.firstName, currentAccount?.lastName) || currentAccount?.email || 'You';
  const creatorAvatar = getAvatarImageSrc(currentAccount?.avatar, 24);

  return (
    <header
      className={cn(
        'ui2026:@container/page-items-stats flex shrink-0 flex-col gap-2 border-b ads:border-b-default border-b-gray-4 px-6 py-4',
        PAGE_HEADER_TOOLBAR_GRID_CLASS,
      )}
    >
      <div className="col-span-full flex min-w-0 flex-col gap-1.5">
        <PrivateWorkspaceAgentAssetListTitle />
        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 ads:text-metadata text-12 text-gray-10 leading-4">
            <span>{capturedAtLabel}</span>
            <span aria-hidden className="ads:text-icon-subtle text-gray-8">
              ·
            </span>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {creatorAvatar ? (
                // biome-ignore lint/performance/noImgElement: avatar src is a user image URL, not a next/image remote pattern
                <img alt="" className="size-4 shrink-0 rounded-full object-cover" src={creatorAvatar} />
              ) : null}
              <span className="truncate">{creatorName}</span>
            </span>
          </div>

          <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
            <PrivateWorkspaceAgentAssetListCount />

            <ViewTypeToggleMenuComponent
              availableViewTypeNames={ARTIFACT_LIST_VIEW_TYPES}
              currentViewTypeName={currentViewTypeName}
              dataTestId="WORKSPACE_AGENT_ASSET_LIST_VIEW_TOGGLE"
              onChange={handleChangeView}
            />

            {canPin ? (
              <Tooltip label={isPinned ? 'Unpin' : 'Pin to thread'} side="bottom" sideOffset={4}>
                <IconButton
                  appearance={isPinned ? 'filled' : 'outline'}
                  aria-pressed={isPinned}
                  color="gray"
                  data-testid="WORKSPACE_AGENT_ASSET_LIST_HEADER_PIN"
                  label={isPinned ? 'Unpin' : 'Pin to thread'}
                  onClick={handleTogglePin}
                  size="small"
                >
                  <PinIcon aria-hidden className={cn(isPinned && 'fill-current')} size="xs" />
                </IconButton>
              </Tooltip>
            ) : null}

            <Button
              appearance="outline"
              className="shrink-0 gap-1 font-medium"
              color="gray"
              data-testid="WORKSPACE_AGENT_ASSET_LIST_HEADER_SAVE_AS_BOARD"
              disabled={savedBoard != null || (clipIds.length === 0 && !hasUnresolvedRows)}
              onClick={handleSaveAsBoard}
              size="small"
              type="button"
            >
              {savedBoard ? <CheckIcon aria-hidden size="xs" /> : null}
              {getSaveAsBoardActionLabel(savedBoard?.boardTitle)}
            </Button>
          </div>
        </div>
      </div>

      {isSaveModalOpen && artifactId ? (
        <SaveWorkspaceAgentAssetListAsBoardModal
          {...(hasUnresolvedRows ? { artifactId, sessionId, workspaceId } : {})}
          clipIds={clipIds}
          onClose={handleCloseSaveModal}
          onSaved={handleSavedAsBoard}
          title={storedTitle || WORKSPACE_AGENT_ASSET_LIST_LABEL}
          trackLocation="workspace-agent-panel"
        />
      ) : null}
    </header>
  );
});

PrivateWorkspaceAgentAssetListHeader.displayName = 'PrivateWorkspaceAgentAssetListHeader';
